import type { ReactNode } from 'react';
import emptyIlustration from '@/assets/svg/empty-illustration.svg';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface EmptyStateProps {
  /** Título principal exibido abaixo do ícone */
  readonly title: string;
  /** Descrição opcional com mais contexto */
  readonly description?: string | undefined;
  /** Ação opcional (ex: botão de criar) renderizada abaixo da descrição */
  readonly action?: ReactNode | undefined;
  /** URL/import de SVG para substituir a ilustração padrão */
  readonly illustration?: string | undefined;
  /**
   * Versão compacta — ícone menor e menos espaçamento vertical, sem sombra.
   * Use em estados vazios aninhados (ex.: dentro de uma seção de accordion já
   * expandida), onde o tamanho padrão fica desproporcional ao contexto.
   */
  readonly compact?: boolean;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Molecule EmptyState — estado vazio genérico.
 *
 * Exibe um ícone ilustrativo, título e descrição opcional para indicar
 * ausência de dados em tabelas, listas e painéis.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title={t('table.empty')}
 *   description={t('table.emptyDescription')}
 * />
 *
 * // Aninhado dentro de uma seção já expandida (ex.: linha de accordion aberta)
 * <EmptyState compact title={t('subTable.empty')} description={t('subTable.emptyDescription')} />
 * ```
 */
export function EmptyState({
  title,
  description,
  action,
  illustration,
  compact = false,
}: EmptyStateProps) {
  return (
    <div
      className={[
        'flex w-full flex-col items-center justify-center gap-xxxs rounded-md bg-ctx-layout-surface text-center',
        compact ? 'py-lg' : 'py-xxl shadow-level1',
      ].join(' ')}
    >
      {/* Ícone ilustrativo */}
      <img
        className={compact ? 'size-xl' : 'size-xxxl'}
        src={illustration ?? emptyIlustration}
        alt=""
      />

      {/* Texto */}
      <div className="flex flex-col gap-nano">
        <p className="text-ctx-content-title ts-subtitle-xs">{title}</p>
        {description && (
          <p className="max-w-xs mx-auto text-ctx-content-placeholder text-center ts-caption-xxs">
            {description}
          </p>
        )}
      </div>

      {action}
    </div>
  );
}
